/**
 * tool-display-registry
 *
 * Per-tool overrides for the chat tool-call card. The backend supplies
 * `descriptive_name` (header label, via @explain / register_tool_display
 * in sourcepilot/models/tasks/tools/decorators.py). The frontend handles
 * everything visual — icons, subtitle JSX, bespoke result bodies — here.
 *
 * Add an entry only when:
 *  - the tool wants a non-default icon, or
 *  - the subtitle needs to pull from a specific args/response field, or
 *  - the result body needs a custom layout
 */

import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { BookOpen, MessageCircleQuestion, Globe, Search } from 'lucide-react';

/** Collapsible web-search results — collapsed by default (just a "Show N
 *  results" toggle); expands to the title links + source domain + snippet.
 *  A component (not inline) so it can hold its own open/closed state. */
function WebSearchResults({ items }: { items: any[] }): React.ReactElement | null {
  const [open, setOpen] = React.useState(false);
  const h = React.createElement;
  if (items.length === 0) return null;

  const toggle = h(
    'button',
    {
      type: 'button',
      onClick: (e: any) => {
        e.stopPropagation();
        setOpen((o) => !o);
      },
      className: 'mt-1 text-xs text-gray-400 hover:text-gray-600',
    },
    open
      ? `Hide results`
      : `Show ${items.length} result${items.length === 1 ? '' : 's'}`,
  );
  if (!open) return toggle;

  const list = h(
    'div',
    { className: 'mt-2 space-y-3' },
    items.slice(0, 8).map((r: any, i: number) => {
      let domain = '';
      try {
        domain = r?.href ? new URL(r.href).hostname.replace(/^www\./, '') : '';
      } catch {
        domain = '';
      }
      return h(
        'div',
        { key: i, className: 'text-sm' },
        r?.href
          ? h(
              'a',
              {
                href: r.href,
                target: '_blank',
                rel: 'noreferrer',
                className: 'font-medium text-indigo-600 hover:underline',
              },
              r.title || r.href,
            )
          : h('span', { className: 'font-medium text-gray-900' }, r?.title || '(untitled)'),
        // Source domain — these are DuckDuckGo result snippets; the page
        // behind the link is NOT fetched.
        domain ? h('div', { className: 'text-xs text-gray-400' }, domain) : null,
        r?.body ? h('p', { className: 'mt-0.5 text-gray-600' }, r.body) : null,
      );
    }),
  );
  return h('div', null, toggle, list);
}

/** Defensively coerce a tool response into an array of items (the response
 *  may arrive as a parsed array or as a JSON string). */
function _asList(response: unknown): any[] {
  if (Array.isArray(response)) return response;
  if (typeof response === 'string') {
    try {
      const parsed = JSON.parse(response);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export interface ToolDisplayContext {
  args: any;
  response: any;
}

export interface ToolDisplay {
  icon?: LucideIcon;
  /**
   * Header label override. Wins over the backend `descriptive_name` and the
   * formatted tool name. Use when the backend label is unreliable (e.g. a
   * tool whose label registers lazily) or just unfriendly.
   */
  name?: string;
  /**
   * Inline subtitle rendered next to `descriptive_name` in the header
   * (e.g. "Activate Skill — Functional Decomposition"). Receives partial
   * args during streaming and full response after completion. Return
   * null/undefined to let the default <PrimaryEntityLabel> handle entity
   * responses.
   */
  subtitle?: (ctx: ToolDisplayContext) => React.ReactNode;
  /**
   * Separator between the tool name and the subtitle. Default is an em-dash
   * ("Activate Skill — Functional Decomposition"); set e.g. ":" to read
   * "Web Search: …" (the colon hugs the name, no em-dash).
   */
  separator?: string;
  /**
   * Optional replacement for the tool-result body. Defaults to
   * <ToolResultCard/>.
   */
  resultBody?: (ctx: ToolDisplayContext & { isError: boolean }) => React.ReactNode;
}

const REGISTRY: Record<string, ToolDisplay> = {
  activate_skill: {
    icon: BookOpen,
    // skill_display_name comes from Skill.display_label on the server.
    // args.skill_name (kebab slug) shows only in the brief window
    // before the response arrives, and in pre-existing history cards
    // whose stored response predates skill_display_name.
    subtitle: ({ args, response }) =>
      response?.skill_display_name ?? args?.skill_name ?? null,
    // The subtitle already says which skill became active; the response
    // payload is LLM-facing metadata and would otherwise show as a
    // meaningless "Result: N items returned".
    resultBody: () => null,
  },
  deactivate_skill: {
    icon: BookOpen,
    subtitle: ({ response }) =>
      response?.skill_display_name ?? response?.skill ?? null,
    resultBody: () => null,
  },
  ask_user_a_question: {
    icon: MessageCircleQuestion,
  },
  // Entity search ("Search: …"). Args: `{ text }`; response: an array of
  // `{ id, _label, name, description }`. Shows the search text + a collapsible
  // list of matched entities (reuses the query-results renderer).
  search_rflp_entities: {
    icon: Search,
    name: 'Search',
    separator: ':',
    subtitle: ({ args }) => {
      const t = args?.text;
      return typeof t === 'string' && t ? `“${t}”` : null;
    },
    resultBody: ({ response }) => {
      const items = _asList(response);
      if (items.length === 0) return null;
      return React.createElement(QueryResults, { items });
    },
  },
  // Web text search: show the search term in the header, and the actual
  // results (title → link, snippet) in the body instead of "N items returned".
  // Args: `{ query }`; response: `[{ title, href, body }]`. Defensive against
  // partial (streaming) args and string/array response shapes.
  web_search_text: {
    icon: Globe,
    separator: ':',
    subtitle: ({ args }) => {
      const q = args?.query;
      return typeof q === 'string' && q ? `“${q}”` : null;
    },
    // Collapsed by default — just the "Web Search: …" header line plus a
    // "Show N results" toggle that reveals the result links + snippets.
    resultBody: ({ response }) => {
      const items = _asList(response);
      if (items.length === 0) return null;
      return React.createElement(WebSearchResults, { items });
    },
  },
};

const EMPTY: ToolDisplay = {};

// --- Generated query tools (`<Model>_query`) -------------------------------
// One shared display for every auto-generated query tool (LogicalPart_query,
// Requirement_query, …). They have no descriptive_name, so the header is the
// formatted tool name ("Logicalpart Query"); we add the query terms after it
// and a collapsible list of the returned items.

/** Returned items from a query response `{ _status, _message, data: [...] }`
 *  (parsed or JSON string). */
function _queryData(response: unknown): any[] {
  let r: any = response;
  if (typeof r === 'string') {
    try {
      r = JSON.parse(r);
    } catch {
      return [];
    }
  }
  return r && typeof r === 'object' && Array.isArray(r.data) ? r.data : [];
}

/** Collapsed-by-default list of returned entities (name + type label). */
function QueryResults({ items }: { items: any[] }): React.ReactElement | null {
  const [open, setOpen] = React.useState(false);
  const h = React.createElement;
  if (items.length === 0) return null;
  const toggle = h(
    'button',
    {
      type: 'button',
      onClick: (e: any) => {
        e.stopPropagation();
        setOpen((o) => !o);
      },
      className: 'mt-1 text-xs text-gray-400 hover:text-gray-600',
    },
    open ? 'Hide results' : `Show ${items.length} result${items.length === 1 ? '' : 's'}`,
  );
  if (!open) return toggle;
  const list = h(
    'div',
    { className: 'mt-2 space-y-1' },
    items.slice(0, 20).map((it: any, i: number) => {
      const name = it?.name ?? it?.slug ?? it?.id ?? '(unnamed)';
      const label = it?._label ?? it?.type;
      return h(
        'div',
        { key: i, className: 'flex items-baseline gap-2 text-sm' },
        h('span', { className: 'font-medium text-gray-800' }, String(name)),
        label ? h('span', { className: 'text-xs text-gray-400' }, String(label)) : null,
      );
    }),
  );
  return h('div', null, toggle, list);
}

/** Compact "what was queried" string from the query args — the queried
 *  entity (its NAME, resolved from the result, since the raw `id` is an opaque
 *  LLM-scoped integer) and/or filter fields, plus the expanded relationships
 *  (`include`). `data` is the query result rows. */
function _queryTerms(args: any, data: any[] = []): string | null {
  if (!args || typeof args !== 'object') return null;
  const terms: string[] = [];
  for (const [k, v] of Object.entries(args)) {
    if (k === 'explanation' || k === 'include' || v == null) continue;
    if (k === 'id') {
      // An id-query returns the single queried entity — show its name rather
      // than the opaque `#id`. Falls back to `#id` before the result arrives.
      const name = data.length ? data[0]?.name : null;
      terms.push(name ? String(name) : `#${v}`);
      continue;
    }
    terms.push(`${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`);
  }
  const inc =
    args.include && typeof args.include === 'object' && !Array.isArray(args.include)
      ? Object.keys(args.include)
      : [];
  let s = terms.join(', ');
  if (inc.length) s = s ? `${s} · ${inc.join(', ')}` : inc.join(', ');
  return s || null;
}

const QUERY_TOOL_DISPLAY: ToolDisplay = {
  icon: Search,
  separator: ':',
  subtitle: ({ args, response }) => _queryTerms(args, _queryData(response)),
  resultBody: ({ response }) => {
    const items = _queryData(response);
    if (items.length === 0) return null;
    return React.createElement(QueryResults, { items });
  },
};

export function getToolDisplay(toolName: string | null | undefined): ToolDisplay {
  if (!toolName) return EMPTY;
  const exact = REGISTRY[toolName];
  if (exact) return exact;
  // Generated query tools share one display (pattern, not per-tool).
  if (toolName.endsWith('_query')) return QUERY_TOOL_DISPLAY;
  return EMPTY;
}
